import React, { createContext, useContext, useState, useEffect } from 'react';

export const THEMES = [
  {
    id: 'ocean',
    name: 'Oceaan',
    emoji: '🌊',
    desc: 'Helder oceaanblauw — de standaard',
  },
  {
    id: 'forest',
    name: 'Woud',
    emoji: '🌲',
    desc: 'Donker bos met gouden zonlicht',
  },
  {
    id: 'sunset',
    name: 'Zonsondergang',
    emoji: '🌅',
    desc: 'Vurig rood en oranje avondgloed',
  },
  {
    id: 'beach',
    name: 'Strand',
    emoji: '🏖️',
    desc: 'Hemelsblauw met zandgouden tinten',
  },
  {
    id: 'northern',
    name: 'Noorderlicht',
    emoji: '🌌',
    desc: 'Paars en groen met sterrenhemel',
  },
  {
    id: 'custom',
    name: 'Mijn stijl',
    emoji: '✨',
    desc: 'Zelf gegenereerd via AI',
  },
];

// CSS variable names mapped from the JSON keys Claude returns
const VAR_MAP = {
  brand:       '--brand',
  brand_dark:  '--brand-dark',
  brand_dark2: '--brand-dark2',
  brand_deep:  '--brand-deep',
  brand_light: '--brand-light',
  bg_page:     '--bg-page',
  brand_rgb:   '--brand-rgb',
  sidebar_bg:  '--sidebar-bg',
  chat_bg:     '--chat-bg',
  chat_orb_1:  '--chat-orb-1',
  chat_orb_2:  '--chat-orb-2',
  chat_orb_3:  '--chat-orb-3',
  chat_orb_4:  '--chat-orb-4',
  chat_bubble: '--chat-bubble',
  chat_shadow: '--chat-shadow',
  chat_avatar: '--chat-avatar',
};

function applyCustomVars(vars) {
  const el = document.documentElement;
  Object.entries(VAR_MAP).forEach(([key, cssVar]) => {
    if (vars[key]) el.style.setProperty(cssVar, vars[key]);
  });

  // Derive glow + hover-shadow from brand_rgb if present
  if (vars.brand_rgb) {
    const r = vars.brand_rgb;
    el.style.setProperty('--brand-glow',   `rgba(${r}, 0.40)`);
    el.style.setProperty('--hover-shadow', `rgba(${r}, 0.12)`);

    // Derive hero-overlay-bg from brand_rgb — atmospheric wide banner gradient
    // Uses the brand colour as atmospheric light source blended over a near-black base
    el.style.setProperty('--hero-overlay-bg',
      `radial-gradient(ellipse at 10% 50%, rgba(${r}, 0.48) 0%, transparent 55%),` +
      `radial-gradient(ellipse at 82% 15%, rgba(${r}, 0.28) 0%, transparent 45%),` +
      `radial-gradient(ellipse at 50% 90%, rgba(${r}, 0.55) 0%, transparent 52%),` +
      `radial-gradient(ellipse at 85% 80%, rgba(255,255,255,0.06) 0%, transparent 35%),` +
      `linear-gradient(135deg, rgba(0,0,0,0.96) 0%, rgba(${r},0.82) 48%, rgba(0,0,0,0.94) 100%)`
    );
  }
}

function clearCustomVars() {
  const el = document.documentElement;
  const allVars = [...Object.values(VAR_MAP), '--brand-glow', '--hover-shadow', '--hero-overlay-bg'];
  allVars.forEach(v => el.style.removeProperty(v));
}

const ThemeContext = createContext({
  theme: 'ocean',
  setTheme: () => {},
  customTheme: null,
  setCustomTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('tripdash_theme') || 'ocean';
  });

  const [customTheme, setCustomThemeState] = useState(() => {
    const stored = localStorage.getItem('tripdash_custom_theme');
    return stored ? JSON.parse(stored) : null;
  });

  const setTheme = (id) => {
    setThemeState(id);
    localStorage.setItem('tripdash_theme', id);
  };

  const setCustomTheme = (vars) => {
    setCustomThemeState(vars);
    localStorage.setItem('tripdash_custom_theme', JSON.stringify(vars));
    // If we're already on custom, apply immediately
    if (theme === 'custom') applyCustomVars(vars);
  };

  // Apply data-theme attribute and custom vars whenever theme changes
  useEffect(() => {
    const el = document.documentElement;
    if (theme === 'custom') {
      el.setAttribute('data-theme', 'custom');
      if (customTheme) applyCustomVars(customTheme);
    } else {
      clearCustomVars();
      el.setAttribute('data-theme', theme);
    }
  }, [theme, customTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, customTheme, setCustomTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
